import PropTypes from 'prop-types';
import { FieldArray, FormikProvider, useFormik } from 'formik';
import { useSelector, useDispatch } from 'react-redux';
import { useEffect, useState } from 'react';
import { Locations, Input, FormTitle, PropertyType, Spinner } from 'components';
import { appSetInviteClientAddressesAction } from 'store/actions/app';
import { getInvitedClientAddresses, getInvitedClientValues } from 'store/selectors/app';
import { PENDING } from 'settings/constants/apiState';
import { InviteHeader } from '../../../Headers';
import { schema } from './validation';
import { DatePicker, Button } from 'components';
import { Tooltip } from 'components-antd';
import styles from './styles.module.scss';
import { AddNew, TooltipIcon, Edit, Add } from 'components/Icons';
import classNames from 'classnames';
import { Checkbox } from 'antd';
import { ClientCategory } from 'types';
import { inviteClientSteps } from 'types/inviteClient';
import { Icons } from 'pages/ClientProfile/icons';
import moment from 'moment';
import { showErrorMessage } from 'helpers';
import { getCampaigns } from 'api/radarKits';
import { BlueLocationIcon } from './Icons';
import KitsSequences from './components/KitsSequences';
import { v4 as UUID } from 'uuid';
import ClientAddresses from './components/ClientAddresses';
import {
  getClientDrawerSendingInvite,
  getActiveClientTransactionInstance,
  getInvitedClientRole,
} from 'store/selectors/app';

enum Steps {
  EnterDetails = 0,
  ShowKits = 1,
  ShowAddressesWithCampaigns = 2,
  ShowAddressesWithoutCampaigns = 3,
}

const InviteClientRetentionMode = ({ onNext, onSubmit, stageIndex }) => {
  const dispatch = useDispatch();
  const { state } = useSelector(getClientDrawerSendingInvite);
  const role = useSelector(getInvitedClientRole);
  const activeClientTransactionInstance = useSelector(getActiveClientTransactionInstance);
  const { firstName } = useSelector(getInvitedClientValues);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [internalStep, setInternalStep] = useState<Steps>(Steps.EnterDetails);
  const buyerRole = role.includes(ClientCategory.Buyer);
  const [allCampaigns, setAllCampaigns] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const isPending = state === PENDING;
  const clientAddresses = useSelector(getInvitedClientAddresses);

  useEffect(() => {
    if (clientAddresses?.length)
      formik.setFieldValue(
        `addresses`,
        clientAddresses.map((address) => ({
          ...address,
          uuid: UUID(),
          noteEnabled: !!address.notes,
        })),
      );
  }, [clientAddresses]);

  useEffect(() => {
    fetchCampaigns();
  }, []);

  const fetchCampaigns = async () => {
    setLoading(true);
    try {
      const response = await getCampaigns();
      if (response.status === 200 && response?.data?.result?.length)
        setAllCampaigns(response.data.result);
    } catch (e) {
      showErrorMessage('Unable to fetch campaigns & KITs');
    }
    setLoading(false);
  };

  const formik = useFormik({
    initialValues:
      Object.keys(activeClientTransactionInstance).length > 0
        ? {
            FromRetentionMode: true,
            addresses: [
              {
                address: activeClientTransactionInstance.address,
                notes: '',
                radius: 1,
                closingDate: null,
                propertyType: [],
                isPrimary: true,
                noteEnabled: false,
                campaignIds: [],
                uuid: UUID(),
              },
            ],
          }
        : InviteClientRetentionMode.initialValues,
    validationSchema: schema,
    onSubmit: (values) => {
      dispatch(
        appSetInviteClientAddressesAction(
          values.addresses.map(
            ({ address, closingDate, notes, radius, propertyType, isPrimary, campaignIds }) => ({
              address,
              closingDate: closingDate?.[0]
                ? moment(closingDate[0]).startOf('day').toISOString()
                : null,
              ...(radius && { neighbourhoodRadius: parseFloat(String(radius)) }),
              ...(propertyType?.length ? { propertyType } : {}),
              notes,
              isPrimary,
              ...(campaignIds.length && { CampaignIds: campaignIds }),
            }),
          ),
        ),
      );
      if (buyerRole) {
        onNext(inviteClientSteps.ClientSearchInstance);
      } else {
        onSubmit({
          onNext,
          formValues: {
            FromRetentionMode: true,
          },
        });
        onNext(inviteClientSteps.ClientInvite);
      }
    },
  });

  const disabled = !formik?.values?.addresses?.[selectedIndex]?.address?.delivery_line_1;

  const onPrev = () => {
    if (internalStep > 0) {
      if (
        internalStep === Steps.ShowAddressesWithoutCampaigns &&
        !formik.values.addresses.filter((address) => address.campaignIds.length).length
      )
        setInternalStep(Steps.ShowKits);
      else setInternalStep(internalStep - 1);
    } else if (selectedIndex > 0) {
      setSelectedIndex(selectedIndex - 1);
      setInternalStep(3);
    } else onNext(inviteClientSteps.ClientDetails);
  };

  const getFormattedAddress = (address) => {
    let formattedAddress = '-';
    if (address?.delivery_line_1) formattedAddress = address.delivery_line_1;
    if (formattedAddress) {
      if (address?.components?.city_name) formattedAddress += ', ' + address.components.city_name;
      if (address?.components?.state_abbreviation)
        formattedAddress += ', ' + address.components.state_abbreviation;
    }
    return formattedAddress;
  };

  const selectionChangeHandle = (ids: number[]) => {
    formik.setFieldValue(`addresses[${selectedIndex}].campaignIds`, ids?.length ? ids : []);
  };

  const disableButton = (): boolean => {
    if (isPending || loading || !formik.isValid || disabled) return true;

    return false;
  };

  const handleContinue = () => {
    if (internalStep < 3) setInternalStep(internalStep + 1);
    else if (selectedIndex < formik.values.addresses.length - 1) {
      setSelectedIndex(selectedIndex + 1);
      setInternalStep(0);
    }
    if (internalStep === Steps.ShowAddressesWithoutCampaigns) formik.handleSubmit();
  };

  const handleSkip = () => {
    formik.setFieldValue(`addresses[${selectedIndex}].campaigns`, []);
    setInternalStep(Steps.ShowAddressesWithCampaigns);
  };

  const handleAddAddress = () => {
    formik.setFieldValue('addresses', [
      ...formik.values.addresses,
      { ...InviteClientRetentionMode.initialValues.addresses[0], uuid: UUID(), isPrimary: false },
    ]);
    setSelectedIndex(formik.values.addresses.length);
    setInternalStep(Steps.EnterDetails);
  };

  const editAddressHandler = (uuid: string) => {
    const index = formik.values.addresses.findIndex((address) => address.uuid === uuid);
    if (index !== -1) {
      setSelectedIndex(index);
      setInternalStep(Steps.EnterDetails);
    }
  };

  return (
    <form
      testid="invite_retention_form"
      className={styles.fullWidth}
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      <InviteHeader
        disabled={isPending || loading}
        stageIndex={stageIndex}
        onPrev={onPrev}
        isValid={formik.isValid}
      />
      {loading ? (
        <Spinner />
      ) : (
        <>
          {internalStep === Steps.EnterDetails && (
            <div className={styles.formContainer}>
              <FormTitle className={styles.titleHeading}>
                {('What is ' + firstName + "'s" + ' address?') as string}
              </FormTitle>
              <FormikProvider value={formik}>
                <FieldArray
                  name={'addresses'}
                  render={(arrayHelpers) => (
                    <>
                      <div className={styles.propertyInformation}>
                        {selectedIndex !== 0 && (
                          <span
                            className={styles.remove}
                            onClick={() => {
                              arrayHelpers.remove(selectedIndex);
                              setSelectedIndex(selectedIndex - 1);
                            }}
                          >
                            <Icons variant={Icons.REMOVE} />
                          </span>
                        )}
                        <Locations
                          name="listingAddress"
                          onResult={(r, preparedData) => {
                            formik.setFieldValue(
                              `addresses[${selectedIndex}].address`,
                              preparedData?.length ? preparedData[0] : [],
                            );
                          }}
                          label="Address"
                          placeholder="Enter Address"
                          testid="listing_address"
                          value={
                            formik?.values?.addresses?.[selectedIndex]?.address
                              ? [formik.values.addresses[selectedIndex]?.address]
                              : []
                          }
                          multiple={false}
                          activeInputIconClassName={styles.locationsIcon}
                          showAsterisk={true}
                          variant={Locations.ROUND}
                          placeholderClassName={styles.border}
                          searchWrapperClassName={styles.border}
                          valuesWrapperClassName={styles.border}
                          dropdownClassName={styles.dropdown}
                          rounded={true}
                          shouldFilterCityLevelResults={true}
                          shouldFilterStreetLevelResults={true}
                          allowedSmartyAddress={true}
                          showOnlyCassAddresses={true}
                          setFullCassAddress={true}
                        />
                        {!disabled && (
                          <>
                            <Checkbox
                              type="checkbox"
                              name="isPrimary"
                              className={classNames(styles.checkbox, 'mosaikCheckbox')}
                              value={formik.values?.addresses?.[selectedIndex]?.isPrimary}
                              onChange={(e) => {
                                const isChecked = e.target.checked;
                                const updatedAddresses = formik.values?.addresses?.map(
                                  (address, index) => ({
                                    ...address,
                                    isPrimary: index === selectedIndex ? isChecked : false,
                                  }),
                                );
                                formik.setFieldValue('addresses', updatedAddresses);
                              }}
                              checked={formik.values?.addresses?.[selectedIndex]?.isPrimary}
                            >
                              Primary Address
                            </Checkbox>
                            <div className={styles.row}>
                              <DatePicker
                                value={formik.values?.addresses?.[selectedIndex]?.closingDate}
                                className={styles.inputContainer}
                                label="Date of Closing"
                                variant={DatePicker.ROUNDED}
                                options={{ enableTime: false }}
                                onChange={(e) => {
                                  formik.setFieldValue(
                                    `addresses[${selectedIndex}].closingDate`,
                                    e.target.value,
                                  );
                                }}
                                allowTypedInput={true}
                                allowPlugin={false}
                                format={'m/d/Y'}
                                placeholder={'MM/DD/YYYY'}
                              />
                              <Input
                                disabled={isPending || loading}
                                className={styles.inputContainer}
                                name={`addresses[${selectedIndex}].radius`}
                                isNumberFormat={true}
                                minNumber={0}
                                maxNumber={10}
                                variant={Input.LIGHT_ROUND}
                                onChange={(e) => {
                                  let newValue = parseFloat(e.target.value);
                                  formik.setFieldValue(
                                    `addresses.[${selectedIndex}].radius`,
                                    newValue || 0,
                                  );
                                }}
                                value={formik.values?.addresses?.[selectedIndex]?.radius || ''}
                                label="Neighborhood Activity Radius"
                                placeholder=""
                                icon={<p className={styles.iconRight}>Miles</p>}
                                decimalScale={2}
                                allowLeadingZeros={false}
                                labelRightIcon={
                                  <Tooltip
                                    placement="top"
                                    overlayClassName={classNames(
                                      styles.radiusTooltip,
                                      'mosaikTooltip',
                                    )}
                                    title={
                                      'This is the radius that will be used to provide neighborhood activity updates to the client (for example: nearby sales, price reductions, new listings, etc.).'
                                    }
                                  >
                                    <span>
                                      <TooltipIcon
                                        className={styles.tooltipIcon}
                                        size={'17'}
                                        color={'#AAABAB'}
                                      />
                                    </span>
                                  </Tooltip>
                                }
                              />
                            </div>
                            <PropertyType
                              onChange={(value) => {
                                formik.setFieldValue(
                                  `addresses[${selectedIndex}].propertyType`,
                                  value,
                                );
                              }}
                              value={formik.values.addresses?.[selectedIndex]?.propertyType}
                              type="dropdown"
                              label={'Property Type (optional)'}
                              className={styles.propertyType}
                            />
                            {formik.values?.addresses?.[selectedIndex]?.noteEnabled ? (
                              <Input
                                disabled={isPending || loading}
                                className={classNames(styles.inputContainer, styles.notes)}
                                name={`addresses[${selectedIndex}].notes`}
                                variant={Input.LIGHT_ROUND}
                                onChange={formik.handleChange}
                                value={formik.values?.addresses?.[selectedIndex]?.notes}
                                label="Notes"
                                placeholder=""
                                textArea={true}
                              />
                            ) : (
                              <div
                                className={styles.addNote}
                                onClick={() => {
                                  formik.setFieldValue(
                                    `addresses.[${selectedIndex}].noteEnabled`,
                                    true,
                                  );
                                }}
                              >
                                <AddNew />
                                Add Notes
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </>
                  )}
                />
              </FormikProvider>
            </div>
          )}
          {internalStep === Steps.ShowKits && (
            <div className={styles.kitsContainer}>
              <div className={styles.addressContainer}>
                <div className={styles.left}>
                  <BlueLocationIcon />
                </div>
                <div className={styles.middle}>
                  {formik.values?.addresses?.[selectedIndex]?.isPrimary && (
                    <div className={styles.primary}>Primary Address</div>
                  )}
                  <div className={styles.address}>
                    {getFormattedAddress(formik.values?.addresses?.[selectedIndex]?.address)}
                  </div>
                </div>
                <div className={styles.right} onClick={() => setInternalStep(Steps.EnterDetails)}>
                  <Edit stroke={'#676767'} size={'18'} strokeWidth={'2'} />
                </div>
              </div>
              <KitsSequences
                campaigns={allCampaigns}
                selectionChangeHandle={selectionChangeHandle}
                selectedCampaigns={formik.values?.addresses?.[selectedIndex]?.campaignIds}
              />
            </div>
          )}
          {internalStep === Steps.ShowAddressesWithCampaigns ||
          internalStep === Steps.ShowAddressesWithoutCampaigns ? (
            <ClientAddresses
              addresses={formik.values.addresses}
              editAddressHandler={editAddressHandler}
              campaigns={allCampaigns}
              withCampaigns={internalStep === Steps.ShowAddressesWithCampaigns}
              handleContinue={handleContinue}
            />
          ) : null}
          <div
            className={classNames(
              styles.footer,
              internalStep === Steps.EnterDetails ? styles.footerCenter : styles.footerBetween,
            )}
          >
            {internalStep === Steps.ShowKits && (
              <Button title="Skip" className={styles.skip} onClick={handleSkip} />
            )}
            {internalStep === Steps.ShowAddressesWithCampaigns ||
            internalStep === Steps.ShowAddressesWithoutCampaigns ? (
              <div className={styles.addAnother} onClick={handleAddAddress}>
                <Add color={Add.ORANGE_CIRCLE} />
                Add Another Address
              </div>
            ) : null}
            <Button
              disabled={disableButton()}
              title={'Continue'}
              className={classNames(
                styles.continue,
                internalStep === Steps.EnterDetails ? styles.continueBig : styles.continueSmall,
              )}
              onClick={handleContinue}
            />
          </div>
        </>
      )}
    </form>
  );
};

InviteClientRetentionMode.initialValues = {
  FromRetentionMode: true,
  addresses: [
    {
      address: null,
      notes: '',
      radius: 1,
      closingDate: null,
      propertyType: [],
      isPrimary: true,
      noteEnabled: false,
      campaignIds: [],
      uuid: '',
    },
  ],
};

InviteClientRetentionMode.propTypes = {
  onNext: PropTypes.func,
  onPrev: PropTypes.func,
  stageIndex: PropTypes.number,
};

InviteClientRetentionMode.defaultProps = {
  onNext: () => {},
  onPrev: () => {},
  stageIndex: 1,
};

export default InviteClientRetentionMode;
